import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLogs } from '../services/api';

const STAGES = [
	{ value: '', label: 'All Stages' },
	{ value: 'crm_search', label: 'CRM Search' },
	{ value: 'public_crm_search', label: 'Public CRM Search' },
	{ value: 'kount_nao', label: 'Kount NAO' },
	{ value: 'public_kount_nao', label: 'Public Kount NAO' },
	{ value: 'kount_otp_validate', label: 'OTP Validation' },
	{ value: 'kount_otp_resend', label: 'OTP Resend' },
	{ value: 'equifax_report', label: 'Equifax Report' },
	{ value: 'public_equifax_report', label: 'Public Equifax Report' },
	{ value: 'public_reprocess_check', label: 'Reprocess Check' },
	{ value: 'public_create_claims_crm', label: 'Create Claims' },
	{ value: 'public_credit_check_init', label: 'Credit Check Init' },
];

export default function AuditLogsPage() {
	const [logs, setLogs] = useState([]);
	const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
	const [filters, setFilters] = useState({ search: '', stage: '', status: '', dateFrom: '', dateTo: '' });
	const [expandedRow, setExpandedRow] = useState(null);
	const [loading, setLoading] = useState(true);

	const fetchLogs = useCallback(async (page = 1) => {
		setLoading(true);
		// Reset pagination immediately to avoid stale display
		setPagination(prev => ({ ...prev, page }));
		try {
			const data = await getAuditLogs({ ...filters, page, limit: 50 });
			setLogs(data.logs || []);
			setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
		} catch (err) {
			console.error('Failed to fetch logs:', err);
			setLogs([]);
			setPagination({ page: 1, totalPages: 1, total: 0 });
		} finally {
			setLoading(false);
		}
	}, [filters]);

	// When filters change, always reset to page 1
	useEffect(() => { fetchLogs(1); }, [fetchLogs]);

	function handleFilterChange(field) {
		return (e) => {
			setFilters(prev => ({ ...prev, [field]: e.target.value }));
			// pagination will reset via the useEffect -> fetchLogs(1) chain
		};
	}

	function formatResponse(response) {
		try { return JSON.stringify(JSON.parse(response), null, 2); }
		catch { return response; }
	}

	function truncate(str, len = 70) {
		if (!str) return '-';
		return str.length > len ? str.substring(0, len) + '...' : str;
	}

	const PaginationControls = () => (
		<div className="logs-pagination">
			<span className="logs-pagination-info">
				{pagination.total > 0
					? `Showing ${((pagination.page - 1) * 50) + 1}-${Math.min(pagination.page * 50, pagination.total)} of ${pagination.total}`
					: '0 records'}
			</span>
			<div className="logs-pagination-btns">
				<button disabled={pagination.page <= 1} onClick={() => fetchLogs(pagination.page - 1)} className="pagination-btn">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
					Prev
				</button>
				<span className="pagination-page">Page {pagination.page} / {pagination.totalPages || 1}</span>
				<button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchLogs(pagination.page + 1)} className="pagination-btn">
					Next
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
				</button>
			</div>
		</div>
	);

	return (
		<div className="audit-logs-page">
			<h2>Audit Logs</h2>
			<p className="page-subtitle">Monitor all credit check activity and API interactions.</p>

			<div className="logs-card">
				<div className="logs-filters">
					<div className="logs-filter-row">
						<div className="filter-group">
							<label>Search</label>
							<input type="text" value={filters.search} onChange={handleFilterChange('search')} placeholder="CRM ID or Email..." />
						</div>
						<div className="filter-group">
							<label>Stage</label>
							<select value={filters.stage} onChange={handleFilterChange('stage')}>
								{STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
							</select>
						</div>
						<div className="filter-group">
							<label>Status</label>
							<select value={filters.status} onChange={handleFilterChange('status')}>
								<option value="">All</option>
								<option value="success">Success</option>
								<option value="error">Error</option>
								<option value="blocked">Blocked</option>
							</select>
						</div>
						<div className="filter-group">
							<label>From</label>
							<input type="date" value={filters.dateFrom} onChange={handleFilterChange('dateFrom')} />
						</div>
						<div className="filter-group">
							<label>To</label>
							<input type="date" value={filters.dateTo} onChange={handleFilterChange('dateTo')} />
						</div>
					</div>
				</div>

				<PaginationControls />

				{loading ? (
					<div className="logs-loading">
						<div className="loading-spinner" />
						<span>Loading logs...</span>
					</div>
				) : logs.length === 0 ? (
					<div className="logs-empty">
						<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
						<p>No logs found matching your filters.</p>
					</div>
				) : (
					<div className="logs-table-wrap">
						<table className="logs-table">
							<thead>
								<tr>
									<th>CRM Record ID</th>
									<th>Email</th>
									<th>Stage</th>
									<th>Status</th>
									<th>Response</th>
									<th>Time</th>
								</tr>
							</thead>
							<tbody>
								{logs.map(log => (
									<React.Fragment key={log.id}>
										<tr className={`log-row ${log.status}`} onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
											<td className="cell-mono">{log.crmRecordId || '-'}</td>
											<td className="cell-email">{log.email || <span className="cell-muted">-</span>}</td>
											<td><span className="stage-badge">{log.stage}</span></td>
											<td><span className={`status-badge ${log.status}`}>{log.status}</span></td>
											<td className="response-preview">{truncate(log.response)}</td>
											<td className="cell-time">{log.createdTime ? new Date(log.createdTime).toLocaleString() : '-'}</td>
										</tr>
										{expandedRow === log.id && (
											<tr className="expanded-row">
												<td colSpan="6">
													<div className="response-detail-wrap">
														<button
															className="copy-log-btn"
															title="Copy to clipboard"
															onClick={(e) => {
																e.stopPropagation();
																const text = formatResponse(log.response);
																navigator.clipboard.writeText(text).then(() => {
																	const btn = e.currentTarget;
																	btn.textContent = 'Copied!';
																	setTimeout(() => { btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy'; }, 1500);
																});
															}}
														>
															<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
															Copy
														</button>
														<pre className="response-detail">{formatResponse(log.response)}</pre>
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				)}

				{!loading && logs.length > 10 && <PaginationControls />}
			</div>
		</div>
	);
}
